/**
 * apps/web :: lib/trial-api
 *
 * Typed wrappers around the `/api/trial` surface (ROADS-445 FE-01).
 *
 * Endpoints:
 *   - GET /api/trial/me  → 200 {@link TrialStatus} | 401 (anonymous) |
 *                          503 when the module is unconfigured.
 *
 * Wire shape mirrors the shared kotlin type
 * `cx.humane.roadbridge.shared.trial.TrialStatus` (snake_case via
 * `@SerialName`). Keeping snake_case avoids the adapter layer the
 * codebase deleted in PR #63.
 *
 * Auth: requires the `rb_session` cookie; `apiFetch` always sends
 * credentials. 401s bubble up as `ApiError`; the caller (typically a
 * route gate or hook) decides whether to redirect to `/login`.
 */
import { apiFetch } from "@/lib/api";

/**
 * Snapshot of a user's free-trial state at the server's wall-clock
 * instant. Trust ONLY these timestamps; never compare against
 * `Date.now()` directly — the whole point of the endpoint is that
 * the server is the source of truth for the clock.
 */
export interface TrialStatus {
  /** True iff trial is in force AND no active entitlement. */
  active: boolean;
  /** Epoch-millis when the user's upstream record was created. */
  started_at_ms: number;
  /** Epoch-millis when the trial ends — always started_at_ms + TRIAL_WINDOW_MS. */
  expires_at_ms: number;
  /** Server clock at response build time. */
  now_ms: number;
}

/**
 * Free-trial window — kept in sync with `TrialStatus.TRIAL_WINDOW_MS`
 * in `kmp-shared-core`. Currently 30 days (TEMPORARY while content +
 * functionality are being built out; will be re-tuned alongside the
 * post-content paywall pass).
 */
export const TRIAL_WINDOW_MS: number = 30 * 24 * 60 * 60 * 1000;

/**
 * Show the countdown banner only inside the last N hours of the trial.
 * Left at 6h — the banner is meant to be a "trial is almost up" nudge,
 * not a passive "trial is running" indicator. With a 30-day window the
 * banner now appears in the final 6 hours, same UX as the 24h regime.
 */
export const TRIAL_BANNER_THRESHOLD_MS: number = 6 * 60 * 60 * 1000;

/**
 * Fetch the authenticated user's trial status. Throws `ApiError(401)`
 * for anonymous users (the route gate handles that by redirecting to
 * `/login` rather than the paywall).
 */
export async function getTrialStatus(): Promise<TrialStatus> {
  return apiFetch<TrialStatus>("/api/trial/me");
}
